/* eslint-disable react/jsx-one-expression-per-line */
import React from 'react';
import PropTypes from 'prop-types';
import Select from '$components/Select';
import List, {
  ListItem,
  ListItemPrimaryText,
  ListItemSecondaryText,
  ListItemContent,
} from '$components/List';
import Callout from '$components/Callout';
import { gradeTypeTitles } from '$variables/constants';
import getSampleData from './utils/getSampleData';

import {
  Container,
  Sample,
  SelectLabel,
  CalloutContainer,
} from './Step2.styled';

const Step2 = ({
  fileData,
  emailColumnIndex,
  gradeColumnIndex,
  onSelectEmailColumn,
  onSelectGradeColumn,
  studentsNotInSpace,
  invalidGrades,
  duplicateEmails,
  gradeType,
  hasAnyValidGrades,
}) => {
  const options = fileData[0].map((fieldValue, index) => ({
    name: fieldValue,
    value: index,
  }));

  const emailSample = getSampleData({
    data: fileData,
    columnIndex: emailColumnIndex,
  });
  const gradeSample = getSampleData({
    data: fileData,
    columnIndex: gradeColumnIndex,
  });

  return (
    <Container>
      <SelectLabel>CSV column for student emails</SelectLabel>
      <Select
        value={emailColumnIndex}
        options={options}
        placeholder="Pick a column"
        onChange={(_, value) => onSelectEmailColumn(value)}
      />
      <Sample visible={emailSample.length > 0}>
        Sample content: {emailSample.join(', ')}
      </Sample>
      <SelectLabel>CSV column for grades</SelectLabel>
      <Select
        value={gradeColumnIndex}
        options={options}
        placeholder="Pick a column"
        onChange={(_, value) => onSelectGradeColumn(value)}
      />
      <Sample visible={gradeSample.length > 0}>
        Sample content: {gradeSample.join(', ')}
      </Sample>
      {!hasAnyValidGrades && !invalidGrades.length && (
        <CalloutContainer>
          <Callout type="error">
            None of the email addresses in the selected column of the CSV match
            the students enrolled in this space.
          </Callout>
        </CalloutContainer>
      )}
      {studentsNotInSpace.length > 0 && (
        <>
          <CalloutContainer>
            <Callout>
              The students below are listed in the CSV but are not enrolled in
              this space. Their grades will be ignored.
            </Callout>
          </CalloutContainer>
          <List>
            {studentsNotInSpace.map(({ email }, idx) => (
              // eslint-disable-next-line react/no-array-index-key
              <ListItem key={`${email}-${idx}`}>
                <ListItemPrimaryText>{email}</ListItemPrimaryText>
              </ListItem>
            ))}
          </List>
        </>
      )}
      {duplicateEmails.length > 0 && (
        <>
          <CalloutContainer>
            <Callout type="error">
              The students below are listed in the CSV more than once. Please
              remove duplicates and try again.
            </Callout>
          </CalloutContainer>
          <List>
            {duplicateEmails.map((email) => (
              <ListItem key={email}>
                <ListItemPrimaryText>{email}</ListItemPrimaryText>
              </ListItem>
            ))}
          </List>
        </>
      )}
      {invalidGrades.length > 0 && (
        <>
          <CalloutContainer>
            <Callout type="error">
              The grades for the students listed below did not match the grade
              type of the assignment ({gradeTypeTitles[gradeType]}):
            </Callout>
          </CalloutContainer>
          <List>
            {invalidGrades.map(({ email, firstName, lastName }) => (
              <ListItem key={email}>
                <ListItemContent>
                  <ListItemPrimaryText>
                    {firstName} {lastName}
                  </ListItemPrimaryText>
                  <ListItemSecondaryText>{email}</ListItemSecondaryText>
                </ListItemContent>
              </ListItem>
            ))}
          </List>
        </>
      )}
    </Container>
  );
};

Step2.propTypes = {
  fileData: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.string)).isRequired,
  onSelectEmailColumn: PropTypes.func.isRequired,
  onSelectGradeColumn: PropTypes.func.isRequired,
  emailColumnIndex: PropTypes.number,
  gradeColumnIndex: PropTypes.number,
  studentsNotInSpace: PropTypes.arrayOf(
    PropTypes.shape({
      email: PropTypes.string.isRequired,
    })
  ).isRequired,
  invalidGrades: PropTypes.arrayOf(
    PropTypes.shape({
      email: PropTypes.string.isRequired,
      firstName: PropTypes.string.isRequired,
      lastName: PropTypes.string.isRequired,
    })
  ).isRequired,
  duplicateEmails: PropTypes.arrayOf(PropTypes.string).isRequired,
  gradeType: PropTypes.string.isRequired,
  hasAnyValidGrades: PropTypes.bool.isRequired,
};

Step2.defaultProps = {
  emailColumnIndex: undefined,
  gradeColumnIndex: undefined,
};

export default Step2;
